import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from "axios";
import { API } from '../../config/api';
import { Util } from '../../util/util';
const initialState = {
    signupSuccess: false,
    googleConfig: null,
    googleAuthLoading: false,
    facebookConfig: null,
    facebookAuthLoading: false,
    recaptchaConfig: null,
    userObject: {}
}

export const doSignup = createAsyncThunk("signUp/submit",
 async (data) => {
    // First, register the user with recaptchaToken in request body
    const signupResponse = await axios.post(API.signup, {
        givenName: data.givenName,
        mobileNumber: data.mobileNumber,
        email: data.email,
        password: data.password,
        recaptchaToken: data.recaptchaToken
    });
    
    // If signup was successful, automatically log the user in
    if (signupResponse?.data?.data) {
        const loginResponse = await axios.post(API.login, {
            email: data.email,
            password: data.password
        });
        return loginResponse?.data;
    }
    
    return signupResponse?.data;
});

export const fetchGoogleConfig = createAsyncThunk("signUp/googleConfig",
 async () => {
    const response = await axios.get(API.googleConfig);
    return response?.data;
});

export const doGoogleSignup = createAsyncThunk("signUp/googleAuth",
 async (credential) => {
    const response = await axios.post(API.oAuth, { oauth_token : credential, provider: "google" });
    return response?.data;
});

export const fetchFacebookConfig = createAsyncThunk("signUp/facebookConfig",
 async () => {
    const response = await axios.get(API.facebookConfig, { silentError: true });
    return response?.data;
});

export const doFacebookSignup = createAsyncThunk("signUp/facebookAuth",
 async (accessToken) => {
    const response = await axios.post(API.oAuth, { oauth_token: accessToken, provider: "facebook" });
    return response?.data;
});

export const fetchRecaptchaConfig = createAsyncThunk("signUp/recaptchaConfig",
 async () => {
    const response = await axios.get(API.recaptchaConfig, { silentError: true });
    return response?.data;
});

const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {},
  extraReducers(builder) {
        builder
            .addCase(doSignup.fulfilled, (state, action) => {
                if(action?.payload?.data){
                    Util.showAlert(Util.alert_types.success, 'CONGRATS!', 'Thanks for signing up' );
                    state.signupSuccess = true;
                    state.userObject = action.payload.data;
                    localStorage.setItem('userObject', JSON.stringify(action.payload.data));
                }else{
                    Util.showAlert(Util.alert_types.error, 'ERROR', 'Technical Error!' );
                } 
            })
            .addCase(fetchGoogleConfig.fulfilled, (state, action) => {
                // Handle both { data: {...} } and direct { clientId, redirectUri, scope } responses
                const config = action?.payload?.data || action?.payload;
                if(config?.clientId){
                    state.googleConfig = config;
                }
            })
            .addCase(fetchGoogleConfig.rejected, (state, action) => {
                console.error('Failed to fetch Google config:', action.error);
            })
            .addCase(doGoogleSignup.pending, (state) => {
                state.googleAuthLoading = true;
            })
            .addCase(doGoogleSignup.fulfilled, (state, action) => {
                state.googleAuthLoading = false;
                if(action?.payload?.data){
                    Util.showAlert(Util.alert_types.success, 'CONGRATS!', 'Successfully signed up with Google!' );
                    state.signupSuccess = true;
                    state.userObject = action.payload.data;
                    localStorage.setItem('userObject', JSON.stringify(action.payload.data));
                }else{
                    Util.showAlert(Util.alert_types.error, 'ERROR', 'Google signup failed!' );
                }
            })
            .addCase(doGoogleSignup.rejected, (state, action) => {
                state.googleAuthLoading = false;
                console.error('Google signup failed:', action.error);
                Util.showAlert(Util.alert_types.error, 'ERROR', 'Google signup failed!' );
            })
            .addCase(fetchFacebookConfig.fulfilled, (state, action) => {
                const config = action?.payload?.data || action?.payload;
                if(config?.appId){
                    state.facebookConfig = config;
                }
            })
            .addCase(fetchFacebookConfig.rejected, (state, action) => {
                console.error('Failed to fetch Facebook config:', action.error);
            })
            .addCase(doFacebookSignup.pending, (state) => {
                state.facebookAuthLoading = true;
            })
            .addCase(doFacebookSignup.fulfilled, (state, action) => {
                state.facebookAuthLoading = false;
                if(action?.payload?.data){
                    Util.showAlert(Util.alert_types.success, 'CONGRATS!', 'Successfully signed up with Facebook!' );
                    state.signupSuccess = true;
                    state.userObject = action.payload.data;
                    localStorage.setItem('userObject', JSON.stringify(action.payload.data));
                }else{
                    Util.showAlert(Util.alert_types.error, 'ERROR', 'Facebook signup failed!' );
                }
            })
            .addCase(doFacebookSignup.rejected, (state, action) => {
                state.facebookAuthLoading = false;
                console.error('Facebook signup failed:', action.error);
                Util.showAlert(Util.alert_types.error, 'ERROR', 'Facebook signup failed!' );
            })
            .addCase(fetchRecaptchaConfig.fulfilled, (state, action) => {
                const config = action?.payload?.data || action?.payload;
                if(config?.siteKey){
                    state.recaptchaConfig = config;
                    // Load reCAPTCHA script dynamically
                    if (window.loadRecaptcha) {
                        window.loadRecaptcha(config.siteKey);
                    }
                }
            })
            .addCase(fetchRecaptchaConfig.rejected, (state, action) => {
                console.error('Failed to fetch reCAPTCHA config:', action.error);
            })
    }
});

export const selectsignupSuccess = (state) => state.signup.signupSuccess;
export const selectGoogleConfig = (state) => state.signup.googleConfig;
export const selectGoogleAuthLoading = (state) => state.signup.googleAuthLoading;
export const selectFacebookConfig = (state) => state.signup.facebookConfig;
export const selectFacebookAuthLoading = (state) => state.signup.facebookAuthLoading;
export const selectRecaptchaConfig = (state) => state.signup.recaptchaConfig;
export const selectSignupUser = (state) => state.signup.userObject;

export default signupSlice.reducer;